import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import useSheetStack from "../common/useSheetStack";
import SheetStack from "../common/SheetStack";
import { ACT_V2_ADD, ACT_V2_EDIT, ACT_V2_STATUS } from "./events";
import ActivityForm from "./ActivityForm";
import ActivityStatusForm from "./ActivityStatusForm";

/**
 * Physical-activity editor rail — a stack of write sheets, newest on top.
 *
 * Board 3071:4236 draws the editor as a bordered card in the third column
 * with no scrim, which is the rail idiom the other v3 sections already use;
 * it was previously a centred modal.
 *
 * The rail keeps the table's current rows because the form's duplicate check
 * runs against them on every keystroke. They arrive on the open event rather
 * than from a GET of this rail's own — the section already holds the list, so
 * fetching it again here would be a second request for data that is on screen.
 */

const ADD = [ACT_V2_ADD];
// The status channel (board 3071:4666) rides the edit path: both open a
// sheet for a specific record. The payload's `mode` marker survives on the
// record so the renderer below can tell the two sheet kinds apart — the
// channels share one listener, so the marker cannot come from the channel.
const EDIT = [ACT_V2_EDIT, ACT_V2_STATUS];

const getRecord = (payload) => {
  if (!payload || !payload.activity) return null;
  return payload.mode === "status"
    ? { ...payload.activity, __statusOnly: true }
    : payload.activity;
};
// One key for BOTH kinds: an edit sheet and a status sheet for the same row
// would race each other's write, so the same activityId dedups across them.
const getRecordId = (r) => r && r.activityId;
const describeRecord = (r) => (r && r.name) || "activity";
const dupMessage = (r) => `${describeRecord(r)} is already open for update.`;

const ActivityEditor = ({ patient }) => {
  const patientId = patient && (patient.id || patient.patientID);
  const [list, setList] = useState([]);

  const stack = useSheetStack({
    addEvents: ADD,
    editEvents: EDIT,
    getRecord,
    getRecordId,
    blankMessage: "A blank activity form is already open.",
    dupMessage,
    enabled: !!patientId,
  });

  // A second listener on the same channels: useSheetStack consumes the
  // payload's record, this takes the list riding alongside it.
  useEffect(() => {
    const capture = (payload) => {
      if (payload && Array.isArray(payload.existingList)) setList(payload.existingList);
    };
    EventService.addListener(ACT_V2_ADD, capture);
    EventService.addListener(ACT_V2_EDIT, capture);
    return () => {
      EventService.removeListener(ACT_V2_ADD, capture);
      EventService.removeListener(ACT_V2_EDIT, capture);
    };
  }, []);

  if (!patientId) {
    return <div className="ph-slot-empty is-editor"><p>Waiting for patient…</p></div>;
  }

  return (
    <SheetStack
      stack={stack}
      className="pa3-editor"
      describe={(s) =>
        s.editing && s.editing.__statusOnly
          ? `Update status · ${describeRecord(s.editing)}`
          : s.editing
          ? `Edit ${describeRecord(s.editing)}`
          : "Add activity"
      }
    >
      {(sheet, props) =>
        sheet.editing && sheet.editing.__statusOnly ? (
          <ActivityStatusForm patientId={patientId} {...props} />
        ) : (
          <ActivityForm patientId={patientId} existingList={list} {...props} />
        )
      }
    </SheetStack>
  );
};

ActivityEditor.propTypes = {
  patient: PropTypes.object,
};

export default ActivityEditor;
